import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import {
  AllEmployeeGroupsDocument,
  EmployeeGroupAssignModalDataDocument,
  EmployeeGroupRootPageDocument,
  SettingsEmployeeGroupFragment,
  UpdateEmployeeEmployeeGroupDocument,
} from 'generated/resources/graphql'
import { compact } from 'lodash'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelector } from 'modules/employees/components/EmployeeSelector'

interface Props {
  employeeGroup: SettingsEmployeeGroupFragment
  onClose: () => void
}

const AssignModal = ({ onClose, employeeGroup }: Props) => {
  const [loading, setLoading] = React.useState<boolean>(false)
  const [UpdateEmployeeEmployeeGroup] = useMutation(UpdateEmployeeEmployeeGroupDocument, {
    extractorFn: (data) => data.employeesMutations.updateEmployeesEmployee,
  })

  const employeeGroupEmployees = employeeGroup.employees
    .map((employee) => employee.employeeName)
    .filter(Boolean)

  const [selectedEmployees, setSelectedEmployees] =
    React.useState<EmployeeForSelectFragmentNoAccess[]>(employeeGroupEmployees)

  const updateEmployeeEmployeeGroup = (
    employeeId: number,
    employeeGroupId: number,
    legalEntityId: number
  ) => {
    return UpdateEmployeeEmployeeGroup({
      variables: {
        id: employeeId,
        employeeGroupId: employeeGroupId,
        legalEntityId: legalEntityId,
      },
      refetchQueries: [
        AllEmployeeGroupsDocument,
        EmployeeGroupRootPageDocument,
        EmployeeGroupAssignModalDataDocument,
      ],
    })
  }

  const onSelectEmployees = (newEmployees: EmployeeForSelectFragmentNoAccess[]) => {
    setSelectedEmployees(newEmployees)
  }

  const onSubmit = async (closePortal: ClosePortalFn) => {
    setLoading(true)
    const promises = selectedEmployees.map((employee) =>
      updateEmployeeEmployeeGroup(employee.id, employeeGroup.id, employeeGroup.legalEntityId)
    )
    const mutations = await Promise.all(promises)

    const results = mutations.map((mutation) => handleResult(mutation, () => null))
    const firstError = results.find((result) => !result.success)

    if (!firstError) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    } else {
      const errors = firstError.errors
      const messages = compact(
        errors.flatMap((error) => {
          if (error.__typename === 'SimpleError') {
            return error.message
          } else if (error.__typename === 'StructuredError') {
            return error.messages
          }
        })
      )

      app.setFlash({
        type: 'wrong',
        message: messages.join(', '),
      })
    }

    closePortal()
    setLoading(false)
  }

  return (
    <Modal
      illustration="add-user"
      title={i18n.t('settings.legal_entities.legal_entity.employee_group.assign.title', {
        name: employeeGroup.name,
      })}
      description={i18n.t('settings.legal_entities.legal_entity.employee_group.assign.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Button disabled={loading} submit>
          {i18n.t('settings.legal_entities.legal_entity.employee_group.assign.submit')}
        </Button>
      }
    >
      <EmployeeSelector
        withAvatar
        multiple
        alwaysOpen
        value={selectedEmployees}
        onSelect={onSelectEmployees}
      />
    </Modal>
  )
}

export default AssignModal
